import React, { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ArrowUpRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { landingPages } from '../data/landingPages';
import '../styles/ServiceLandingPage.css';

const stripHtml = (str) => (str || '').replace(/<[^>]+>/g, '');

const injectSchema = (page) => {
  const canonical = `https://sanjib-roy.com/${page.slug}/`;
  const cleanH1 = stripHtml(page.hero.h1);
  const service = {
    '@context': 'https://schema.org',
    '@type': page.schemaType === 'LocalBusiness' ? 'LocalBusiness' : 'Service',
    name: cleanH1,
    description: page.meta.description,
    url: canonical,
    provider: {
      '@type': 'Person',
      name: 'Sanjib Roy',
      jobTitle: 'Performance Marketing Consultant',
      url: 'https://sanjib-roy.com/',
    },
  };
  if (page.schemaType === 'LocalBusiness') {
    service.address = {
      '@type': 'PostalAddress',
      addressLocality: 'Kolkata',
      addressRegion: 'West Bengal',
      addressCountry: 'IN',
    };
    service.areaServed = ['Kolkata', 'India', 'Global'];
  }
  const faqSection = page.sections.find((s) => s.type === 'faq');
  const faq = faqSection && {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSection.items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sanjib-roy.com/' },
      { '@type': 'ListItem', position: 2, name: cleanH1, item: canonical },
    ],
  };
  return [service, faq, breadcrumb].filter(Boolean);
};

const ServiceLandingPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Derive slug from route param OR from pathname when route is registered without :slug param
  const slug = paramSlug || location.pathname.replace(/^\/|\/$/g, '');
  const page = landingPages[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // SEO: title, meta description, canonical, JSON-LD schema
  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    document.title = page.meta.title;

    const upsertMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return { el, prev };
    };

    const canonicalHref = `https://sanjib-roy.com/${page.slug}/`;
    const descRes = upsertMeta('description', page.meta.description);
    const keywordsRes = upsertMeta('keywords', page.meta.keywords);
    const ogTitleRes = upsertMeta('og:title', page.meta.title, true);
    const ogDescRes = upsertMeta('og:description', page.meta.description, true);
    const ogUrlRes = upsertMeta('og:url', canonicalHref, true);
    const ogTypeRes = upsertMeta('og:type', 'website', true);
    const twCardRes = upsertMeta('twitter:card', 'summary_large_image');
    const twTitleRes = upsertMeta('twitter:title', page.meta.title);
    const twDescRes = upsertMeta('twitter:description', page.meta.description);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    let prevCanonicalHref = null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    } else {
      prevCanonicalHref = canonical.getAttribute('href');
    }
    canonical.setAttribute('href', canonicalHref);

    // JSON-LD schema (Service/LocalBusiness + FAQPage + BreadcrumbList)
    const schemaBlobs = injectSchema(page);
    const injected = schemaBlobs.map((blob, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-slp-schema', `${page.slug}-${i}`);
      script.text = JSON.stringify(blob);
      document.head.appendChild(script);
      return script;
    });

    // Remove the global homepage FAQPage schema on this interior page (restore on unmount)
    const globalFaqEl = document.getElementById('global-faq-schema');
    const globalFaqContent = globalFaqEl ? globalFaqEl.textContent : null;
    if (globalFaqEl) globalFaqEl.remove();

    const restoreMeta = ({ el, prev }) => {
      if (el && prev !== null) el.setAttribute('content', prev);
    };

    return () => {
      document.title = prevTitle;
      restoreMeta(descRes);
      restoreMeta(keywordsRes);
      restoreMeta(ogTitleRes);
      restoreMeta(ogDescRes);
      restoreMeta(ogUrlRes);
      restoreMeta(ogTypeRes);
      restoreMeta(twCardRes);
      restoreMeta(twTitleRes);
      restoreMeta(twDescRes);
      if (canonical && prevCanonicalHref !== null) canonical.setAttribute('href', prevCanonicalHref);
      injected.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
      if (globalFaqContent && !document.getElementById('global-faq-schema')) {
        const restored = document.createElement('script');
        restored.type = 'application/ld+json';
        restored.id = 'global-faq-schema';
        restored.text = globalFaqContent;
        document.head.appendChild(restored);
      }
    };
  }, [page]);

  if (!page) {
    return (
      <div>
        <Header />
        <div style={{ padding: '160px 24px 120px', textAlign: 'center' }}>
          <h1>Service page not found</h1>
          <button
            onClick={() => navigate('/')}
            style={{ marginTop: 20, padding: '12px 24px', borderRadius: 10, background: '#0ea5e9', color: 'white', border: 0, cursor: 'pointer' }}
          >
            Back to home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const styleVars = {
    '--accent': page.theme.accent,
    '--accent-soft': page.theme.accentSoft,
    '--accent-strong': page.theme.accentStrong,
  };

  return (
    <div className="slp" style={styleVars} data-testid={`slp-${page.slug}`}>
      <Header />

      {/* HERO */}
      <section className="slp-hero">
        <div className="slp-container slp-hero-grid">
          <div>
            <div className="slp-kicker">{page.hero.kicker}</div>
            <h1
              className="slp-h1"
              dangerouslySetInnerHTML={{ __html: page.hero.h1 }}
              data-testid={`slp-h1-${page.slug}`}
            />
            <p className="slp-sub">{page.hero.sub}</p>
            <div className="slp-cta-row">
              <Link to={page.hero.ctaPrimary.href} className="slp-btn-primary" data-testid={`slp-cta-${page.slug}`}>
                {page.hero.ctaPrimary.label} <ArrowRight size={18} />
              </Link>
              <a href={page.hero.ctaSecondary.href} className="slp-btn-ghost">
                {page.hero.ctaSecondary.label}
              </a>
            </div>
            <div className="slp-hero-stats">
              {page.hero.stats.map((s, i) => (
                <div key={i} className="slp-hero-stat">
                  <div className="slp-hero-stat-num">{s.num}</div>
                  <div className="slp-hero-stat-lbl">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="slp-hero-visual">
            <img src={page.heroImage} alt={stripHtml(page.hero.h1)} loading="eager" />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="slp-trust-bar">
        <div className="slp-trust-inner">
          <span>16+ years</span>
          <span>·</span>
          <span>9 countries served</span>
          <span>·</span>
          <span>D2C · B2B SaaS · Ed-Tech</span>
          <span>·</span>
          <span>Kolkata · India · Global</span>
        </div>
      </div>

      {/* SECTIONS */}
      {page.sections.map((section, idx) => {
        const alt = idx % 2 === 1;
        return (
          <section
            key={idx}
            id={section.anchor || undefined}
            className={`slp-section ${alt ? 'alt' : ''}`}
          >
            <div className="slp-container">
              <div className="slp-section-header">
                {section.eyebrow && <div className="slp-section-eyebrow">{section.eyebrow}</div>}
                <h2 className="slp-h2">{section.h2}</h2>
                {section.type === 'prose' && (
                  <div className="slp-prose">
                    {section.body.map((p, i) => (
                      <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                  </div>
                )}
              </div>

              {section.type === 'pillars' && (
                <div className="slp-pillars">
                  {section.items.map((item, i) => (
                    <div key={i} className="slp-pillar">
                      <div className="slp-pillar-num">{item.num}</div>
                      <h3>{item.title}</h3>
                      <p>{item.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {section.type === 'checklist' && (
                <div className="slp-check-grid">
                  {section.items.map((item, i) => (
                    <div key={i} className="slp-check-item">
                      <CheckCircle2 size={20} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {section.type === 'process' && (
                <div className="slp-process">
                  {section.steps.map((step, i) => (
                    <div key={i} className="slp-step">
                      <div className="slp-step-num">{step.num}</div>
                      <h4>{step.title}</h4>
                      <p>{step.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {section.type === 'faq' && (
                <div className="slp-faq">
                  {section.items.map((item, i) => (
                    <details key={i}>
                      <summary>{item.q}</summary>
                      <p>{item.a}</p>
                    </details>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* RELATED */}
      {page.related && page.related.length > 0 && (
        <section className="slp-section alt">
          <div className="slp-container">
            <div className="slp-section-header">
              <div className="slp-section-eyebrow">Related Services</div>
              <h2 className="slp-h2">Continue reading</h2>
            </div>
            <div className="slp-related">
              {page.related.map((r) => (
                <Link key={r.slug} to={`/${r.slug}/`}>
                  <h4>
                    {r.title} <ArrowUpRight size={16} style={{ verticalAlign: 'middle' }} />
                  </h4>
                  <p>{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="slp-cta-band">
        <div className="slp-container">
          <h2>Ready to see the leaks in your growth stack?</h2>
          <p>Book a free 30-minute diagnostic. If there is no leverage to unlock, I will tell you on the call.</p>
          <Link to="/#contact" className="slp-btn-primary">
            Book a Diagnostic <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceLandingPage;
